import HttpService from '@frontend/AppSettings/services/HttpService';
import {
  pathTimesheetDateLock,
  pathTimesheetDateLockPreset,
  pathTimesheetDateLocks,
  pathTimesheetDayLocks,
} from '@frontend/__settings__/septSoftwarePaths';

/**
 * Timesheet day locks (plans/todo/timesheet-day-locks.md, B2) and date locks
 * (plans/todo/timesheet-date-locks.md, B2). Weekdays are Sunday = 0 … Saturday = 6, the same
 * convention as `tenants.workweek_start_day` and JavaScript's `Date.getDay()`.
 * Errors are thrown as `HttpError` (status + the server's `data.error` sentence).
 */
interface Envelope<T> {
  successful: boolean;
  data: T;
}

/** One locked date (`date`, yyyy-MM-dd) or one month/day every year (`month` + `day`). */
export interface DateLock {
  lockId: string;
  date: string | null;
  month: number | null;
  day: number | null;
  label: string;
  source: 'manual' | 'preset';
}

export interface DayLocksPayload {
  lockedWeekdays: number[];
  /** Recurrences by month/day first, then one-offs by date. [] before migration 0107. */
  lockedDates: DateLock[];
}

export interface DayLocksSaveResult {
  lockedWeekdays: number[];
  /** Pending requests auto-cancelled because their weekday was just unlocked. */
  cancelledRequests: number;
}

export interface NewDateLock {
  date?: string;
  month?: number;
  day?: number;
  label: string;
}

export interface DateLockCreateResult {
  lock: DateLock;
  lockedDates: DateLock[];
}

export interface DateLockDeleteResult {
  lockedDates: DateLock[];
  /** Pending requests auto-cancelled because no remaining lock covers their date. */
  cancelledRequests: number;
}

export interface DateLockPresetResult {
  added: number;
  /** Dates of the preset that were already locked. */
  skipped: number;
  lockedDates: DateLock[];
}

const http = new HttpService();

class TimesheetDayLockService {
  /**
   * Any signed-in user. A server without migration 0093 answers no locks; without 0107, no
   * `lockedDates`. The name predates date locks: it resolves the whole lock payload, and
   * `useDayLocks` also accepts a bare weekday array (the shape it resolved before date locks).
   */
  static async getLockedWeekdays(): Promise<DayLocksPayload | number[]> {
    const response = await http.get<Envelope<Partial<DayLocksPayload>>>(pathTimesheetDayLocks);
    return {
      lockedWeekdays: Array.isArray(response?.data?.lockedWeekdays) ? response.data.lockedWeekdays : [],
      lockedDates: Array.isArray(response?.data?.lockedDates) ? response.data.lockedDates : [],
    };
  }

  /**
   * Administrators only (permish 1); anyone else gets a 403. Replaces the whole set, so the set the
   * admin was looking at goes along as `expectedLockedWeekdays`: if another admin changed the locks
   * meanwhile the server answers 409 and writes nothing, instead of silently unlocking (and
   * cancelling the requests of) a weekday this tab never showed as locked.
   */
  static async setLockedWeekdays(lockedWeekdays: number[], expectedLockedWeekdays: number[]): Promise<DayLocksSaveResult> {
    const response = await http.put<Envelope<DayLocksSaveResult>>(pathTimesheetDayLocks, {
      lockedWeekdays,
      expectedLockedWeekdays,
    });
    return response.data;
  }

  /** Administrators only. A date or recurrence that is already locked is a 409 naming it. */
  static async addDateLock(lock: NewDateLock): Promise<DateLockCreateResult> {
    const response = await http.post<Envelope<DateLockCreateResult>>(pathTimesheetDateLocks, lock);
    return response.data;
  }

  /** Administrators only. Cancels pending requests no remaining lock covers. */
  static async deleteDateLock(lockId: string): Promise<DateLockDeleteResult> {
    const response = await http.delete<Envelope<DateLockDeleteResult>>(pathTimesheetDateLock(lockId));
    return response.data;
  }

  /** Administrators only. Re-applying a year adds nothing (`skipped` counts the dates already locked). */
  static async applyPreset(preset: string, year: number): Promise<DateLockPresetResult> {
    const response = await http.post<Envelope<DateLockPresetResult>>(pathTimesheetDateLockPreset, { preset, year });
    return response.data;
  }
}

export default TimesheetDayLockService;
